import { useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth';
import { ApiError, forgotPassword } from '../lib/api';

export const LoginPage = () => {
  const { login } = useAuth();
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (mode === 'forgot') {
        await forgotPassword(email);
        setNotice('If an account exists for that email, a reset link is on its way.');
      } else {
        await login(email, password);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : mode === 'forgot' ? 'Could not send reset link' : 'Could not sign in');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--p-background, #f6f7f9)',
        fontFamily: 'var(--font-product)',
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          width: 360,
          background: '#fff',
          border: '1px solid var(--p-border)',
          borderRadius: 12,
          boxShadow: 'var(--p-shadow-lg)',
          padding: 28,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <img src="/assets/5axis-icon.svg" alt="5 Axis" style={{ width: 28, height: 28 }} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>5 Axis Billing</div>
            <div style={{ fontSize: 12, color: 'var(--p-muted-fg)' }}>
              {mode === 'forgot' ? 'Reset your password' : 'Sign in to the billing console'}
            </div>
          </div>
        </div>

        <label style={labelStyle}>Email</label>
        <input
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={inputStyle}
        />

        {mode === 'login' && (
          <>
            <label style={{ ...labelStyle, marginTop: 14 }}>Password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={inputStyle}
            />
          </>
        )}

        {notice && (
          <div style={{ marginTop: 14, padding: '8px 10px', borderRadius: 6, background: 'rgba(16,185,129,.08)', color: 'var(--status-success-ink,#047857)', fontSize: 12 }}>
            {notice}
          </div>
        )}

        {error && (
          <div
            style={{
              marginTop: 14,
              padding: '8px 10px',
              borderRadius: 6,
              background: 'rgba(239,68,68,.08)',
              color: 'var(--status-error, #b91c1c)',
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          style={{
            marginTop: 18,
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: 'none',
            background: 'var(--p-primary, #2563eb)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? (mode === 'forgot' ? 'Sending…' : 'Signing in…') : mode === 'forgot' ? 'Send reset link' : 'Sign in'}
        </button>

        <button
          type="button"
          onClick={() => { setMode(mode === 'forgot' ? 'login' : 'forgot'); setError(null); setNotice(null); }}
          style={{ marginTop: 14, width: '100%', background: 'none', border: 'none', color: 'var(--p-muted-fg)', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer' }}
        >
          {mode === 'forgot' ? '← Back to sign in' : 'Forgot password?'}
        </button>
      </form>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--p-muted-fg)',
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  border: '1px solid var(--p-border)',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};
